/**
 * ForceChangePassword
 *
 * Spec: spec-force-password-change.md
 * Tela de TROCA OBRIGATÓRIA de senha (1º acesso com senha temporária) do app
 * INTERNO. Renderizada como rota top-level (sem sidebar). O ProtectedRoute
 * redireciona pra cá enquanto `password_change_required` estiver true.
 *
 * Fluxo: exige senha atual (a temporária) + nova senha → POST /users/me/change-password
 * → limpa o flag no contexto e volta pra home. Logout sempre disponível.
 */
import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, KeyRound, Loader2, LogOut } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { userProfileService } from "@/services/userProfileService";

const ForceChangePassword: React.FC = () => {
  const { user, isAuthenticated, isLoading, logout, clearPasswordChangeRequired } = useAuth();
  const navigate = useNavigate();

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Durante a rehidratação do auth, evita flash-redirect pro login.
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-muted-foreground">
        Carregando...
      </div>
    );
  }
  // Sem auth → login. Sem o flag → não há o que trocar, volta pra home.
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  if (!user?.password_change_required) {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (newPassword.length < 8) {
      setError("A nova senha deve ter no mínimo 8 caracteres.");
      return;
    }
    if (!/[A-Z]/.test(newPassword) || !/[a-z]/.test(newPassword) || !/[0-9]/.test(newPassword)) {
      setError("A senha deve conter ao menos uma maiúscula, uma minúscula e um número.");
      return;
    }
    if (newPassword !== confirm) {
      setError("As senhas não coincidem.");
      return;
    }
    setSubmitting(true);
    try {
      await userProfileService.changeMyPassword({
        current_password: currentPassword,
        new_password: newPassword,
      });
      clearPasswordChangeRequired();
      navigate("/", { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Não foi possível trocar a senha. Tente novamente."
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 px-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <KeyRound className="h-5 w-5" />
            Trocar senha
          </CardTitle>
          <CardDescription>
            Por segurança, defina uma nova senha antes de continuar. Você está usando uma senha
            temporária.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="current-password">Senha atual (temporária)</Label>
              <Input
                id="current-password"
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="new-password">Nova senha</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                autoComplete="new-password"
                disabled={submitting}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm-password">Confirme a nova senha</Label>
              <Input
                id="confirm-password"
                type="password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
                disabled={submitting}
              />
            </div>

            <Button type="submit" className="w-full" disabled={submitting}>
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                "Trocar senha e continuar"
              )}
            </Button>

            <Button
              type="button"
              variant="ghost"
              className="w-full"
              onClick={logout}
              disabled={submitting}
            >
              <LogOut className="mr-2 h-4 w-4" />
              Sair
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default ForceChangePassword;
